import React, { useMemo } from "react";
import { ComponentMap, getMDXComponent } from "mdx-bundler/client";
import Image from "next/image";
import CustomLink from "./Link";
import TOCInline from "./TOCInline";
import Pre from "./Pre";
import Button from "@components/library/Button";
import { HumanloopLogo } from "@components/library/Logos";
import { FAQSection } from "@components/FAQSection";
import { classNames } from "@components/library/utils/classNames";
import NewsletterSignUp from "@components/platform/NewsletterSignUp";
import GPT3SignUp from "./GPT3SignUp";

const Wrapper: React.ComponentType<{ layout: string }> = ({
  layout,
  ...rest
}) => {
  const Layout = require(`./layouts/${layout}`).default;
  return <Layout {...rest} />;
};

const Sm2Columns = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={classNames("grid sm:grid-cols-2 sm:gap-x-32", className)}>
      {children}
    </div>
  );
};
const Caption = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={classNames(
        "not-prose -mt-4 text-13-16 leading-[20px] text-gray-800 dark:text-gray-100",
        className
      )}
    >
      {children}
    </div>
  );
};

// Use this mapper to set custom components for the rendered HTML
// e.g. h1: PageTitle , h2: SmallHeader etc.
export const MDXComponents: ComponentMap = {
  // eslint-disable-next-line jsx-a11y/alt-text
  Image: (props) => <Image {...props} lazyBoundary="600px" />,
  Sm2Columns,
  TOCInline,
  Caption,
  a: CustomLink,
  NotProse: ({ className, children }) => (
    <div className={classNames("not-prose", className)}>{children}</div>
  ),
  InitialParagraph: ({ className, ...props }) => (
    <div
      className={classNames(
        "not-prose text-16-26 text-gray-900 sm:text-18-28",
        className
      )}
    >
      {props.children}
    </div>
  ),

  pre: Pre,
  wrapper: Wrapper,
  Button: (props) => (
    <div className="not-prose">
      <Button {...props} />
    </div>
  ),
  HumanloopLogo,
  FAQSection,
  NewsletterSignUp,
  GPT3SignUp,
};

interface Props {
  layout: string;
  mdxSource: string;
  [key: string]: unknown;
}

export const MDXLayoutRenderer = ({ layout, mdxSource, ...rest }: Props) => {
  const MDXLayout = useMemo(() => getMDXComponent(mdxSource), [mdxSource]);

  return <MDXLayout layout={layout} components={MDXComponents} {...rest} />;
};
